import React, { useState, useEffect } from 'react';
import { 
  Package, 
  Edit, 
  Trash2, 
  Search, 
  Plus, 
  Eye, 
  EyeOff,
  Euro,
  Image as ImageIcon,
  Save,
  X
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import LoadingSpinner from '../LoadingSpinner';
import { adminAPI } from '../../services/api';
import FileUpload from './FileUpload';
import QuantityDiscountManager from './QuantityDiscountManager';
import { getImageUrlWithFallback } from '../../utils/imageUtils';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

const AdminProducts = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    available: '',
    search: ''
  });
  
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [productForm, setProductForm] = useState({
    id: '',
    name: '',
    description: '',
    basePrice: '',
    category: 'liquori',
    image: '',
    quantityDiscounts: [],
    available: true,
    featured: false,
    alcoholContent: '',
    origin: '',
    agingYears: '',
    servingTemperature: '',
    tastingNotes: '',
    ingredients: '',
    nutritionalInfo: '',
    defaultQuoteNotes: '',
    industrialCostsByFormat: {}
  });

  const [availableFormats, setAvailableFormats] = useState([]);
  const [pricingProducts, setPricingProducts] = useState([]); // tutti i prodotti listino per check copertura categorie preventivo
  const [leadCategories, setLeadCategories] = useState([]);   // categorie attive del flusso /preventivo (caricate dal DB)

  const loadPricingProducts = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/liquor-types`);
      if (res.ok) {
        const data = await res.json();
        setPricingProducts(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      // silent
    }
  };

  const loadLeadCategories = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lead-categories`);
      if (res.ok) {
        const data = await res.json();
        // adatta alla shape attesa dal pannello copertura
        const cats = (data?.categories || []).map((c) => ({
          id: c.id,
          label: c.label,
          icon: c.icon || '🍶',
          alcohol: c.default_alcohol,
          category: c.db_category,
          linked_product_id: c.linked_product_id || null,
        }));
        setLeadCategories(cats);
      }
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    loadPricingProducts();
    loadLeadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [filters]);

  // Load formats once on mount (used for cost matrix)
  useEffect(() => {
    const loadFormats = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/formats`);
        if (res.ok) {
          const data = await res.json();
          setAvailableFormats(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        // formats are optional for product form
      }
    };
    loadFormats();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      
      // Call real products API
      const response = await adminAPI.getProducts(filters, 1, 20);
      if (response.success) {
        setProducts(response.products);
      } else {
        throw new Error(response.message || 'Failed to load products');
      }
      
    } catch (error) {
      console.error('Error loading products:', error);
      toast({
        title: "Errore nel caricamento",
        description: "Impossibile caricare i prodotti",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProduct = async (productId, updateData) => {
    try {
      setUpdating(productId);
      
      // Clean and format data for backend
      const cleanedData = {
        name: updateData.name,
        description: updateData.description,
        basePrice: parseFloat(updateData.basePrice),
        category: updateData.category,
        available: updateData.available,
        featured: updateData.featured
      };
      
      // Add optional fields only if they have values
      if (updateData.alcoholContent && updateData.alcoholContent !== '') {
        cleanedData.alcoholContent = parseFloat(updateData.alcoholContent);
      }
      if (updateData.origin && updateData.origin !== '') {
        cleanedData.origin = updateData.origin;
      }
      if (updateData.agingYears && updateData.agingYears !== '') {
        cleanedData.agingYears = parseInt(updateData.agingYears);
      }
      if (updateData.servingTemperature && updateData.servingTemperature !== '') {
        cleanedData.servingTemperature = updateData.servingTemperature;
      }
      if (updateData.tastingNotes && updateData.tastingNotes !== '') {
        cleanedData.tastingNotes = updateData.tastingNotes;
      }
      if (updateData.defaultQuoteNotes && updateData.defaultQuoteNotes !== '') {
        cleanedData.defaultQuoteNotes = updateData.defaultQuoteNotes;
      } else {
        cleanedData.defaultQuoteNotes = '';
      }
      // Industrial costs: always send the dict (even if empty, allows clearing)
      cleanedData.industrialCostsByFormat = updateData.industrialCostsByFormat || {};
      if (updateData.image && updateData.image !== '') {
        cleanedData.image = updateData.image;
      }
      if (updateData.quantityDiscounts) {
        cleanedData.quantityDiscounts = updateData.quantityDiscounts;
      }
      
      // Call real API to update product
      const response = await adminAPI.updateProduct(productId, cleanedData);
      if (response.success) {
        toast({
          title: "Prodotto aggiornato",
          description: "Le modifiche sono state salvate con successo",
        });
        
        setShowEditModal(false);
        setSelectedProduct(null);
        loadProducts(); // Reload products
        loadPricingProducts(); // Reload copertura categorie preventivo
      } else {
        throw new Error(response.message || 'Failed to update product');
      }
      
    } catch (error) {
      console.error('Error updating product:', error);
      toast({
        title: "Errore nell'aggiornamento",
        description: error.message || "Impossibile aggiornare il prodotto",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleCreateProduct = async (productData) => {
    try {
      setUpdating('creating');
      
      // Clean and format data for backend
      const cleanedData = {
        name: productData.name,
        description: productData.description,
        basePrice: parseFloat(productData.basePrice),
        category: productData.category,
        available: productData.available,
        featured: productData.featured
      };
      // Slug id esplicito (utile per le categorie del flusso /preventivo)
      if (productData.id && productData.id !== '') {
        cleanedData.id = String(productData.id).trim().toLowerCase();
      }
      
      // Add optional fields only if they have values
      if (productData.alcoholContent && productData.alcoholContent !== '') {
        cleanedData.alcoholContent = parseFloat(productData.alcoholContent);
      }
      if (productData.origin && productData.origin !== '') {
        cleanedData.origin = productData.origin;
      }
      if (productData.agingYears && productData.agingYears !== '') {
        cleanedData.agingYears = parseInt(productData.agingYears);
      }
      if (productData.servingTemperature && productData.servingTemperature !== '') {
        cleanedData.servingTemperature = productData.servingTemperature;
      }
      if (productData.tastingNotes && productData.tastingNotes !== '') {
        cleanedData.tastingNotes = productData.tastingNotes;
      }
      if (productData.defaultQuoteNotes && productData.defaultQuoteNotes !== '') {
        cleanedData.defaultQuoteNotes = productData.defaultQuoteNotes;
      }
      if (productData.industrialCostsByFormat && Object.keys(productData.industrialCostsByFormat).length > 0) {
        cleanedData.industrialCostsByFormat = productData.industrialCostsByFormat;
      }
      if (productData.image && productData.image !== '') {
        cleanedData.image = productData.image;
      }
      if (productData.quantityDiscounts) {
        cleanedData.quantityDiscounts = productData.quantityDiscounts;
      }
      
      // Call real API to create product
      const response = await adminAPI.createProduct(cleanedData);
      if (response.success) {
        toast({
          title: "Prodotto creato",
          description: `Prodotto "${productData.name}" creato con successo`,
        });
        
        setShowCreateModal(false);
        resetForm();
        loadProducts(); // Reload products
        loadPricingProducts(); // Reload copertura categorie preventivo
      } else {
        throw new Error(response.message || 'Failed to create product');
      }
      
    } catch (error) {
      console.error('Error creating product:', error);
      toast({
        title: "Errore nella creazione",
        description: error.message || "Impossibile creare il prodotto",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto? Questa azione non può essere annullata.')) {
      return;
    }

    try {
      setUpdating(productId);
      
      // Call real API to delete product
      const response = await adminAPI.deleteProduct(productId);
      if (response.success) {
        toast({
          title: "Prodotto eliminato",
          description: "Il prodotto è stato eliminato dal catalogo",
        });
        
        loadProducts(); // Reload products
      } else {
        throw new Error(response.message || 'Failed to delete product');
      }
      
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: "Errore nell'eliminazione",
        description: error.message || "Impossibile eliminare il prodotto",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const toggleAvailability = async (productId, currentAvailability) => {
    try {
      setUpdating(productId);
      
      // Call API to update product availability
      const response = await adminAPI.updateProduct(productId, { available: !currentAvailability });
      if (response.success) {
        toast({
          title: "Disponibilità aggiornata",
          description: `Prodotto ${!currentAvailability ? 'attivato' : 'disattivato'}`,
        });
        
        loadProducts(); // Reload products
      } else {
        throw new Error(response.message || 'Failed to update availability');
      }
      
    } catch (error) {
      console.error('Error toggling availability:', error);
      toast({
        title: "Errore",
        description: error.message || "Impossibile aggiornare la disponibilità",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'liquori': 'bg-purple-100 text-purple-800',
      'grappe': 'bg-amber-100 text-amber-800',
      'amari': 'bg-red-100 text-red-800',
      'gin': 'bg-blue-100 text-blue-800',
      'whisky': 'bg-yellow-100 text-yellow-800',
      'vodka': 'bg-gray-100 text-gray-800',
      'rum': 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryName = (category) => {
    const names = {
      'liquori': 'Liquori',
      'grappe': 'Grappe',
      'amari': 'Amari',
      'gin': 'Gin',
      'whisky': 'Whisky',
      'vodka': 'Vodka',
      'rum': 'Rum'
    };
    return names[category] || category;
  };

  const openEditModal = (product) => {
    setSelectedProduct(product);
    setProductForm({
      id: product.id || '',
      name: product.name || '',
      description: product.description || '',
      basePrice: product.basePrice || '',
      category: product.category || 'liquori',
      image: product.image || '',
      quantityDiscounts: product.quantityDiscounts || [],
      available: product.available ?? true,
      featured: product.featured || false,
      alcoholContent: product.alcoholContent || '',
      origin: product.origin || '',
      agingYears: product.agingYears || '',
      servingTemperature: product.servingTemperature || '',
      tastingNotes: product.tastingNotes || '',
      ingredients: product.ingredients || '',
      nutritionalInfo: product.nutritionalInfo || '',
      defaultQuoteNotes: product.defaultQuoteNotes || '',
      industrialCostsByFormat: product.industrialCostsByFormat || {}
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setProductForm({
      id: '',
      name: '',
      description: '',
      basePrice: '',
      category: 'liquori',
      image: '',
      available: true,
      featured: false,
      alcoholContent: '',
      origin: '',
      agingYears: '',
      servingTemperature: '',
      tastingNotes: '',
      ingredients: '',
      nutritionalInfo: '',
      defaultQuoteNotes: '',
      industrialCostsByFormat: {}
    });
  };

  // Prefill del form di creazione a partire da una categoria del flusso /preventivo
  const openCreateFromLeadCategory = (leadCat) => {
    setProductForm({
      id: leadCat.id,
      name: leadCat.label,
      description: `${leadCat.label} — listino ufficiale per il flusso preventivo (/preventivo).`,
      basePrice: '',
      category: leadCat.category,
      image: '',
      available: true,
      featured: false,
      alcoholContent: String(leadCat.alcohol || ''),
      origin: 'Italia',
      agingYears: '',
      servingTemperature: '',
      tastingNotes: '',
      ingredients: '',
      nutritionalInfo: '',
      defaultQuoteNotes: '',
      industrialCostsByFormat: {}
    });
    setShowCreateModal(true);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = !filters.search || 
      product.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      product.description.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesCategory = !filters.category || product.category === filters.category;
    const matchesAvailability = filters.available === '' || 
      (filters.available === 'true' && product.available) ||
      (filters.available === 'false' && !product.available);
    
    return matchesSearch && matchesCategory && matchesAvailability;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestione Prodotti</h1>
            <p className="mt-2 text-gray-600">Gestisci il catalogo di liquori e distillati</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Prodotto
          </button>
        </div>

        {/* Copertura Categorie Preventivo */}
        <LeadCoverageCheck
          leadCategories={leadCategories}
          pricingProducts={pricingProducts}
          onCreate={openCreateFromLeadCategory}
        />

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cerca</label>
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Nome prodotto..."
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Tutte le categorie</option>
                <option value="liquori">Liquori</option>
                <option value="grappe">Grappe</option>
                <option value="amari">Amari</option>
                <option value="gin">Gin</option>
                <option value="whisky">Whisky</option>
                <option value="vodka">Vodka</option>
                <option value="rum">Rum</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Disponibilità</label>
              <select
                value={filters.available}
                onChange={(e) => setFilters(prev => ({ ...prev, available: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Tutti</option>
                <option value="true">Disponibili</option>
                <option value="false">Non disponibili</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ category: '', available: '', search: '' })}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Pulisci Filtri
              </button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {loading ? (
            <div className="text-center py-12">
              <LoadingSpinner message="Caricamento prodotti..." />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product) => (
                <div key={product.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative">
                    <div className="h-48 bg-gray-100 flex items-center justify-center relative">
                      {product.image ? (
                        <img 
                          src={getImageUrlWithFallback(product.image, 'products', product.id)} 
                          alt={product.name}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`flex items-center justify-center h-full w-full ${product.image ? 'hidden' : 'flex'}`}>
                        <div className="text-center">
                          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-xs text-gray-500">Nessuna immagine</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="absolute top-2 right-2 flex space-x-1">
                      {product.featured && (
                        <span className="bg-yellow-500 text-white px-2 py-1 rounded text-xs font-medium">
                          ⭐ In Evidenza
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        product.available 
                          ? 'bg-green-500 text-white' 
                          : 'bg-red-500 text-white'
                      }`}>
                        {product.available ? 'Disponibile' : 'Non disponibile'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(product.category)}`}>
                        {getCategoryName(product.category)}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product.description}</p>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                      <div>💰 €{product.basePrice.toFixed(2)}</div>
                      <div>🍷 {product.alcoholContent}% vol</div>
                      <div>📍 {product.origin}</div>
                      <div>🌡️ {product.servingTemperature}</div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => toggleAvailability(product.id, product.available)}
                        disabled={updating === product.id}
                        className={`flex items-center px-3 py-1 rounded text-sm font-medium transition-colors ${
                          product.available
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {product.available ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                        {product.available ? 'Disattiva' : 'Attiva'}
                      </button>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(product)}
                          disabled={updating === product.id}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Modifica prodotto"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          disabled={updating === product.id}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Elimina prodotto"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filteredProducts.length === 0 && !loading && (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun prodotto trovato</h3>
              <p className="mt-1 text-sm text-gray-500">Prova a modificare i filtri di ricerca</p>
            </div>
          )}
        </div>

        {/* Edit/Create Modal */}
        {(showEditModal || showCreateModal) && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {showCreateModal ? 'Crea Nuovo Prodotto' : `Modifica Prodotto: ${selectedProduct?.name}`}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[70vh] overflow-y-auto">
                  {/* Left Column - Basic Info */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome Prodotto *</label>
                      <input
                        type="text"
                        value={productForm.name}
                        onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione *</label>
                      <textarea
                        value={productForm.description}
                        onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Base *</label>
                        <div className="relative">
                          <Euro className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                          <input
                            type="number"
                            step="0.01"
                            value={productForm.basePrice}
                            onChange={(e) => setProductForm(prev => ({ ...prev, basePrice: e.target.value }))}
                            className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Gradazione Alcolica *</label>
                        <input
                          type="number"
                          step="0.1"
                          value={productForm.alcoholContent}
                          onChange={(e) => setProductForm(prev => ({ ...prev, alcoholContent: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                          placeholder="% vol"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                      <select
                        value={productForm.category}
                        onChange={(e) => setProductForm(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="liquori">Liquori</option>
                        <option value="grappe">Grappe</option>
                        <option value="amari">Amari</option>
                        <option value="gin">Gin</option>
                        <option value="whisky">Whisky</option>
                        <option value="vodka">Vodka</option>
                        <option value="rum">Rum</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">Immagine Prodotto</label>
                      <FileUpload
                        category="products"
                        itemId={productForm.id || 'new'}
                        currentImage={productForm.image}
                        onImageUploaded={(imagePath) => 
                          setProductForm(prev => ({ ...prev, image: imagePath }))
                        }
                      />
                    </div>

                    {/* Quantity Discounts Section */}
                    <div className="md:col-span-2">
                      <QuantityDiscountManager
                        basePrice={parseFloat(productForm.basePrice) || 0}
                        discounts={productForm.quantityDiscounts || []}
                        onChange={(discounts) => 
                          setProductForm(prev => ({ ...prev, quantityDiscounts: discounts }))
                        }
                      />
                    </div>

                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={productForm.available}
                          onChange={(e) => setProductForm(prev => ({ ...prev, available: e.target.checked }))}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Disponibile</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={productForm.featured}
                          onChange={(e) => setProductForm(prev => ({ ...prev, featured: e.target.checked }))}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">In evidenza</span>
                      </label>
                    </div>
                  </div>

                  {/* Right Column - Additional Info */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Origine</label>
                      <input
                        type="text"
                        value={productForm.origin}
                        onChange={(e) => setProductForm(prev => ({ ...prev, origin: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="es. Piemonte, Italia"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Anni di Invecchiamento</label>
                      <input
                        type="number"
                        value={productForm.agingYears}
                        onChange={(e) => setProductForm(prev => ({ ...prev, agingYears: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="0 se non invecchiato"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Temperatura di Servizio</label>
                      <input
                        type="text"
                        value={productForm.servingTemperature}
                        onChange={(e) => setProductForm(prev => ({ ...prev, servingTemperature: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="es. 16-18°C"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Note di Degustazione</label>
                      <textarea
                        value={productForm.tastingNotes}
                        onChange={(e) => setProductForm(prev => ({ ...prev, tastingNotes: e.target.value }))}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Descrivi aroma, gusto e finale..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ingredienti</label>
                      <input
                        type="text"
                        value={productForm.ingredients}
                        onChange={(e) => setProductForm(prev => ({ ...prev, ingredients: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Lista ingredienti separati da virgola"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Note di default per preventivo
                        <span className="text-xs text-gray-500 ml-2">(precompilate quando aggiungi questo prodotto a un preventivo)</span>
                      </label>
                      <textarea
                        value={productForm.defaultQuoteNotes}
                        onChange={(e) => setProductForm(prev => ({ ...prev, defaultQuoteNotes: e.target.value }))}
                        rows={6}
                        data-testid="product-default-quote-notes"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                        placeholder={`es.\n• Tappo: sughero naturale\n• Bottiglia: Bordolese 50cl\n• Capsula: termoretraibile\n• Contrassegno di stato: incluso\n• Accisa di stato: a carico cliente\n• Etichette: 4 colori, carta avorio`}
                      />
                    </div>

                    <div className="md:col-span-2 border-t pt-4 mt-2">
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Costi industriali per formato (€)
                        <span className="text-xs text-gray-500 ml-2 font-normal">(per calcolo MOL su preventivi e ordini)</span>
                      </label>
                      {availableFormats.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">Nessun formato disponibile. Configurali dalla pagina "Formati".</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-gray-200 rounded">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-700">Formato</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-700">Prezzo vendita base</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-700">Costo industriale (€)</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-700">MOL stimato</th>
                              </tr>
                            </thead>
                            <tbody>
                              {availableFormats.map(fmt => {
                                const key = `${fmt.size_cl}cl`;
                                const basePrice = parseFloat(productForm.basePrice || 0);
                                const sellPrice = basePrice * (fmt.price_multiplier || 1);
                                const cost = parseFloat(productForm.industrialCostsByFormat?.[key] ?? 0);
                                const margin = sellPrice - cost;
                                const marginPct = sellPrice > 0 ? (margin / sellPrice) * 100 : 0;
                                const marginColor = margin >= 0 ? 'text-green-700' : 'text-red-600';
                                return (
                                  <tr key={key} className="border-t">
                                    <td className="px-3 py-2 font-medium">{fmt.name || key}</td>
                                    <td className="px-3 py-2 text-right">€ {sellPrice.toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right">
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        data-testid={`industrial-cost-${key}`}
                                        value={productForm.industrialCostsByFormat?.[key] ?? ''}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setProductForm(prev => {
                                            const newCosts = { ...(prev.industrialCostsByFormat || {}) };
                                            if (val === '' || val === null) {
                                              delete newCosts[key];
                                            } else {
                                              newCosts[key] = parseFloat(val);
                                            }
                                            return { ...prev, industrialCostsByFormat: newCosts };
                                          });
                                        }}
                                        placeholder="0.00"
                                        className="w-28 px-2 py-1 border border-gray-300 rounded text-right focus:ring-purple-500 focus:border-purple-500"
                                      />
                                    </td>
                                    <td className={`px-3 py-2 text-right font-semibold ${marginColor}`}>
                                      {cost > 0 ? (
                                        <>€ {margin.toFixed(2)} <span className="text-xs">({marginPct.toFixed(1)}%)</span></>
                                      ) : (
                                        <span className="text-gray-400">—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setShowCreateModal(false);
                      setSelectedProduct(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={() => {
                      if (showCreateModal) {
                        handleCreateProduct(productForm);
                      } else {
                        handleUpdateProduct(selectedProduct.id, productForm);
                      }
                    }}
                    disabled={updating}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50"
                  >
                    {updating ? 'Salvando...' : showCreateModal ? 'Crea Prodotto' : 'Salva Modifiche'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;

// =============================================================
// Pannello "Copertura Categorie /preventivo"
// =============================================================
function LeadCoverageCheck({ leadCategories, pricingProducts, onCreate }) {
  const productMap = React.useMemo(() => {
    const m = new Map();
    (pricingProducts || []).forEach((p) => {
      if (p && p.id) m.set(p.id.toLowerCase(), p);
    });
    return m;
  }, [pricingProducts]);

  const rows = leadCategories.map((lc) => {
    // 1) Match esplicito via linked_product_id (priorità massima)
    // 2) Fallback: match per id slug
    const linkedKey = (lc.linked_product_id || '').toLowerCase();
    const linkedProduct = linkedKey ? productMap.get(linkedKey) : null;
    const slugProduct = productMap.get(lc.id.toLowerCase());
    const product = linkedProduct || slugProduct;
    return { ...lc, product, isLinked: !!linkedProduct };
  });
  const covered = rows.filter((r) => r.product && r.product.basePrice > 0).length;
  const total = rows.length;
  const allCovered = covered === total;

  return (
    <div className="bg-white rounded-lg shadow-md p-6" data-testid="lead-coverage-panel">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            {allCovered ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            )}
            Copertura prezzi — categorie del flusso <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">/preventivo</code>
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Ogni categoria selezionabile in Step 4 deve avere un prodotto con prezzo nel listino, altrimenti in Step 5 apparirà &quot;Prezzo da definire dal nostro team&quot;.
          </p>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${allCovered ? 'text-green-600' : 'text-amber-600'}`} data-testid="lead-coverage-ratio">
            {covered}/{total}
          </div>
          <div className="text-xs text-gray-500">coperte</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 rounded-full bg-gray-100 mb-4 overflow-hidden">
        <div
          className={`h-full ${allCovered ? 'bg-green-500' : 'bg-amber-500'} transition-all`}
          style={{ width: `${Math.round((covered / Math.max(total, 1)) * 100)}%` }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((r) => {
          const ok = !!(r.product && r.product.basePrice > 0);
          return (
            <div
              key={r.id}
              data-testid={`lead-coverage-row-${r.id}`}
              className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${ok ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xl">{r.icon}</span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{r.label}</div>
                  <div className="text-[11px] text-gray-500 font-mono truncate">
                    id: {r.id}
                    {r.isLinked && r.product && (
                      <span className="ml-1 text-blue-700">· 🔗 {r.product.id}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                {ok ? (
                  <div className="flex items-center gap-1.5 text-green-700">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-sm font-bold font-mono">€{Number(r.product.basePrice).toFixed(2)}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onCreate(r)}
                    className="text-xs font-semibold px-2.5 py-1 rounded bg-purple-600 text-white hover:bg-purple-700"
                    data-testid={`lead-coverage-create-${r.id}`}
                  >
                    + Crea
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}